import { useState, useRef, useCallback, useId, useMemo } from 'react';
import { ChevronLeft, ChevronRight, Calendar, X, Phone, Mail, Users } from 'lucide-react';
import { format, addDays, addMonths, subMonths, startOfMonth, isSameDay, parseISO, isWithinInterval, differenceInDays } from 'date-fns';
import { BookingSource, BookingStatus, UserRole } from '@pms/shared';
import { cn } from '@/lib/utils';
import { useAuth } from '@/hooks/useAuth';
import { useCalendar, useCancelBooking, useCheckInBooking, useCheckOutBooking } from '@/hooks/api/useBookings';
import { useProperties } from '@/hooks/api/useProperties';
import { useSelectedPropertyId } from '@/contexts/PropertyContext';

// Re-define local types to replace mock types
interface CalendarBooking {
  id: string;
  unitId: string;
  guestId: string | null;
  guestName?: string;
  guest?: { id: string; fullName: string; email?: string | null; phone?: string | null } | null;
  guestEmail?: string;
  guestPhone?: string;
  checkIn: string;
  checkOut: string;
  source: BookingSource;
  status: BookingStatus;
  baseAmount: string;
  totalAmount: string;
  currency: string;
  guestsCount: number;
  specialRequests?: string | null;
  cancellationReason?: string | null;
}

interface MockUnit {
  id: string;
  name: string;
  propertyId: string;
  propertyName: string;
  maxGuests: number;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const UNIT_COL_WIDTH = 180;
const DAY_COL_WIDTH = 44;
const ROW_HEIGHT = 48;
const DAYS_IN_VIEW = 30;

const STATUS_BG: Record<BookingStatus, string> = {
  [BookingStatus.CONFIRMED]: 'bg-green-500',
  [BookingStatus.PENDING]: 'bg-yellow-400',
  [BookingStatus.CHECKED_IN]: 'bg-blue-500',
  [BookingStatus.CHECKED_OUT]: 'bg-slate-300',
  [BookingStatus.CANCELLED]: 'bg-red-300',
  [BookingStatus.NO_SHOW]: 'bg-orange-300',
};

const STATUS_TEXT: Record<BookingStatus, string> = {
  [BookingStatus.CONFIRMED]: 'text-white',
  [BookingStatus.PENDING]: 'text-yellow-900',
  [BookingStatus.CHECKED_IN]: 'text-white',
  [BookingStatus.CHECKED_OUT]: 'text-slate-600',
  [BookingStatus.CANCELLED]: 'text-red-800',
  [BookingStatus.NO_SHOW]: 'text-orange-900',
};

const STATUS_LABELS: Record<BookingStatus, string> = {
  [BookingStatus.CONFIRMED]: 'Confirmed',
  [BookingStatus.PENDING]: 'Pending',
  [BookingStatus.CHECKED_IN]: 'Checked In',
  [BookingStatus.CHECKED_OUT]: 'Checked Out',
  [BookingStatus.CANCELLED]: 'Cancelled',
  [BookingStatus.NO_SHOW]: 'No Show',
};

const SOURCE_BADGE: Record<BookingSource, { label: string; color: string }> = {
  [BookingSource.AIRBNB]: { label: 'A', color: 'bg-rose-600' },
  [BookingSource.BOOKING_COM]: { label: 'B', color: 'bg-blue-800' },
  [BookingSource.EXPEDIA]: { label: 'E', color: 'bg-yellow-500' },
  [BookingSource.AXIS_ROOMS]: { label: 'AR', color: 'bg-purple-600' },
  [BookingSource.DIRECT]: { label: 'D', color: 'bg-emerald-600' },
  [BookingSource.MANUAL]: { label: 'M', color: 'bg-slate-500' },
};

const SOURCE_LABELS: Record<BookingSource, string> = {
  [BookingSource.AIRBNB]: 'Airbnb',
  [BookingSource.BOOKING_COM]: 'Booking.com',
  [BookingSource.EXPEDIA]: 'Expedia',
  [BookingSource.AXIS_ROOMS]: 'AxisRooms',
  [BookingSource.DIRECT]: 'Direct',
  [BookingSource.MANUAL]: 'Manual',
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

function buildDays(startDate: Date): Date[] {
  return Array.from({ length: DAYS_IN_VIEW }, (_, i) => addDays(startDate, i));
}

/**
 * For a given booking and the visible day range, compute its start column offset
 * (clamped to 0 if it starts before the visible window) and its span width in pixels.
 * Returns null if the booking doesn't intersect the visible window.
 */
function computeBookingLayout(
  booking: CalendarBooking,
  days: Date[],
): { startOffset: number; widthPx: number } | null {
  const checkIn = parseISO(booking.checkIn);
  const checkOut = parseISO(booking.checkOut);
  const windowStart = days[0];
  const windowEnd = days[days.length - 1];

  // booking must overlap the visible window
  const overlapStart = checkIn <= windowEnd && checkOut > windowStart;
  if (!overlapStart) return null;

  // Clamp start to window start
  const visibleStart = checkIn < windowStart ? windowStart : checkIn;
  // Clamp end to window end (exclusive — checkout day cell is the day AFTER the last night)
  const visibleEnd = checkOut > addDays(windowEnd, 1) ? addDays(windowEnd, 1) : checkOut;

  const startOffset = differenceInDays(visibleStart, windowStart);
  const nights = differenceInDays(visibleEnd, visibleStart);

  if (nights <= 0) return null;

  return {
    startOffset,
    widthPx: nights * DAY_COL_WIDTH - 4, // 2px padding each side
  };
}

// ─── Booking Detail Slide-over ────────────────────────────────────────────────

interface SlideOverProps {
  booking: CalendarBooking | null;
  unit: MockUnit | null;
  onClose: () => void;
  onStatusChange: (id: string, status: BookingStatus) => void;
}

function BookingDetailSlideOver({ booking, unit, onClose, onStatusChange }: SlideOverProps) {
  const { user } = useAuth();
  const headingId = useId();
  const [confirmCancel, setConfirmCancel] = useState(false);

  const canEditStatus =
    user?.role === UserRole.ADMIN || user?.role === UserRole.REVENUE_MANAGER;

  if (!booking) return null;

  const totalNights = differenceInDays(parseISO(booking.checkOut), parseISO(booking.checkIn));
  const badge = SOURCE_BADGE[booking.source];

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/40 z-40 transition-opacity"
        aria-hidden="true"
        onClick={onClose}
      />

      {/* Panel */}
      <aside
        role="dialog"
        aria-modal="true"
        aria-labelledby={headingId}
        className="fixed right-0 top-0 h-full w-full max-w-sm bg-white shadow-2xl z-50 flex flex-col"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
          <h2 id={headingId} className="text-base font-semibold text-gray-900">
            Booking Details
          </h2>
          <button
            onClick={onClose}
            aria-label="Close booking details"
            className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 transition-colors"
          >
            <X size={18} aria-hidden="true" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          {/* Guest */}
          <section aria-labelledby="guest-heading">
            <h3 id="guest-heading" className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">
              Guest
            </h3>
            <p className="text-lg font-bold text-gray-900">{booking.guest?.fullName ?? booking.guestName ?? 'Guest'}</p>
            <div className="mt-2 space-y-1.5">
              {booking.guestEmail && (
              <a
                href={`mailto:${booking.guestEmail}`}
                className="flex items-center gap-2 text-sm text-primary-600 hover:underline focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary-500 rounded"
              >
                <Mail size={14} aria-hidden="true" />
                {booking.guestEmail}
              </a>
              )}
              {booking.guestPhone && (
              <a
                href={`tel:${booking.guestPhone}`}
                className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-800 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary-500 rounded"
              >
                <Phone size={14} aria-hidden="true" />
                {booking.guestPhone}
              </a>
              )}
              <p className="flex items-center gap-2 text-sm text-gray-600">
                <Users size={14} aria-hidden="true" />
                {booking.guestsCount} guest{booking.guestsCount !== 1 ? 's' : ''}
              </p>
            </div>
          </section>

          {/* Stay details */}
          <section aria-labelledby="stay-heading">
            <h3 id="stay-heading" className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">
              Stay Details
            </h3>
            <div className="rounded-lg bg-gray-50 p-3 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Unit</span>
                <span className="font-medium text-gray-800">
                  {unit?.name ?? '—'} &middot; {unit?.propertyName ?? '—'}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Check-in</span>
                <span className="font-medium text-gray-800">
                  {format(parseISO(booking.checkIn), 'MMM d, yyyy')}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Check-out</span>
                <span className="font-medium text-gray-800">
                  {format(parseISO(booking.checkOut), 'MMM d, yyyy')}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Nights</span>
                <span className="font-medium text-gray-800">{totalNights}</span>
              </div>
            </div>
          </section>

          {/* Financial */}
          <section aria-labelledby="financial-heading">
            <h3 id="financial-heading" className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">
              Financial
            </h3>
            <div className="rounded-lg bg-gray-50 p-3 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Total Amount</span>
                <span className="font-bold text-gray-900">
                  {booking.currency} {parseFloat(booking.totalAmount).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Source</span>
                <span className="flex items-center gap-1.5 font-medium text-gray-800">
                  <span
                    className={cn('inline-flex items-center justify-center w-4 h-4 rounded text-white text-xs font-bold', badge.color)}
                    aria-hidden="true"
                  >
                    {badge.label}
                  </span>
                  {SOURCE_LABELS[booking.source]}
                </span>
              </div>
            </div>
          </section>

          {/* Status */}
          <section aria-labelledby="status-heading">
            <h3 id="status-heading" className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">
              Status
            </h3>
            {canEditStatus ? (
              <select
                value={booking.status}
                onChange={(e) => onStatusChange(booking.id, e.target.value as BookingStatus)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-800 bg-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                aria-label="Change booking status"
              >
                {Object.values(BookingStatus).map((s) => (
                  <option key={s} value={s}>
                    {STATUS_LABELS[s]}
                  </option>
                ))}
              </select>
            ) : (
              <span
                className={cn(
                  'inline-flex items-center px-3 py-1 rounded-full text-sm font-medium',
                  STATUS_BG[booking.status],
                  STATUS_TEXT[booking.status],
                )}
              >
                {STATUS_LABELS[booking.status]}
              </span>
            )}
          </section>

          {/* Special requests */}
          {booking.specialRequests && (
            <section aria-labelledby="requests-heading">
              <h3 id="requests-heading" className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">
                Special Requests
              </h3>
              <p className="text-sm text-gray-700 bg-amber-50 border border-amber-200 rounded-lg p-3">
                {booking.specialRequests}
              </p>
            </section>
          )}
        </div>

        {/* Footer */}
        {booking.status !== BookingStatus.CANCELLED && (
          <div className="shrink-0 px-5 py-4 border-t border-gray-200">
            {confirmCancel ? (
              <div className="space-y-2">
                <p className="text-sm text-red-700 font-medium text-center">
                  Are you sure you want to cancel this booking?
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      onStatusChange(booking.id, BookingStatus.CANCELLED);
                      setConfirmCancel(false);
                    }}
                    className="flex-1 px-4 py-2 rounded-lg bg-red-600 text-white text-sm font-semibold hover:bg-red-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 transition-colors"
                  >
                    Yes, Cancel
                  </button>
                  <button
                    onClick={() => setConfirmCancel(false)}
                    className="flex-1 px-4 py-2 rounded-lg border border-gray-300 text-gray-700 text-sm font-semibold hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-400 transition-colors"
                  >
                    Keep Booking
                  </button>
                </div>
              </div>
            ) : (
              <button
                onClick={() => setConfirmCancel(true)}
                className="w-full px-4 py-2 rounded-lg border border-red-300 text-red-600 text-sm font-semibold hover:bg-red-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 transition-colors"
              >
                Cancel Booking
              </button>
            )}
          </div>
        )}
      </aside>
    </>
  );
}

// ─── New Booking Toast ────────────────────────────────────────────────────────

function NewBookingToast({
  date,
  unit,
  onDismiss,
}: {
  date: Date;
  unit: MockUnit;
  onDismiss: () => void;
}) {
  return (
    <div
      role="status"
      aria-live="polite"
      className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 bg-gray-900 text-white rounded-xl shadow-xl px-5 py-3 flex items-center gap-4 text-sm"
    >
      <Calendar size={16} aria-hidden="true" className="text-primary-400 shrink-0" />
      <div>
        <span className="font-semibold">New Booking:</span>{' '}
        {unit.name} &middot; {format(date, 'MMM d, yyyy')}
        <span className="ml-2 text-gray-400 text-xs">(booking form coming soon)</span>
      </div>
      <button
        onClick={onDismiss}
        aria-label="Dismiss notification"
        className="p-1 rounded hover:bg-gray-700 transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary-400"
      >
        <X size={14} aria-hidden="true" />
      </button>
    </div>
  );
}

// ─── Main Calendar Component ──────────────────────────────────────────────────

export function ReservationCalendar() {
  const today = new Date();
  const [viewStart, setViewStart] = useState<Date>(startOfMonth(today));
  const [selectedBookingId, setSelectedBookingId] = useState<string | null>(null);
  const [toast, setToast] = useState<{ date: Date; unit: MockUnit } | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const cancelMutation = useCancelBooking();
  const checkInMutation = useCheckInBooking();
  const checkOutMutation = useCheckOutBooking();

  // Fetch real data from API scoped to selected property
  const propertyId = useSelectedPropertyId();
  const viewEnd = addDays(viewStart, 30);
  const { data: calendarData } = useCalendar({
    ...(propertyId ? { propertyId } : {}),
    from: format(viewStart, 'yyyy-MM-dd'),
    to: format(viewEnd, 'yyyy-MM-dd'),
  });
  const { data: propertiesData } = useProperties();

  // Derive units from properties
  const ALL_UNITS: MockUnit[] = useMemo(() => {
    if (!propertiesData) return [];
    const props = Array.isArray(propertiesData) ? propertiesData : (propertiesData.data ?? []);
    return props.flatMap((p: Record<string, unknown>) => {
      const propUnits = Array.isArray(p.units) ? p.units : [];
      return propUnits.map((u: Record<string, unknown>) => ({
        id: (u.id as string) ?? '',
        name: (u.name as string) ?? '',
        propertyId: (p.id as string) ?? '',
        propertyName: (p.name as string) ?? '',
        maxGuests: (u.capacity as number) ?? (u.maxGuests as number) ?? 0,
      }));
    });
  }, [propertiesData]);

  // Flatten calendar data (API returns { [unitId]: Booking[] } or array)
  const bookings: CalendarBooking[] = useMemo(() => {
    if (!calendarData) return [];
    if (Array.isArray(calendarData)) {
      return calendarData as CalendarBooking[];
    }
    // Handle { [unitId]: Booking[] } format
    if (typeof calendarData === 'object' && calendarData !== null) {
      const result: CalendarBooking[] = [];
      for (const [unitId, unitBookings] of Object.entries(calendarData)) {
        if (Array.isArray(unitBookings)) {
          for (const bk of unitBookings) {
            result.push({ ...bk, unitId: bk.unitId ?? unitId } as CalendarBooking);
          }
        }
      }
      if (result.length > 0) return result;
      // May also be { data: [...] }
      if (Array.isArray((calendarData as Record<string, unknown>).data)) {
        return (calendarData as Record<string, unknown>).data as CalendarBooking[];
      }
    }
    return [];
  }, [calendarData]);

  const days = buildDays(viewStart);

  const selectedBooking = bookings.find((b) => b.id === selectedBookingId) ?? null;
  const selectedUnit = selectedBooking
    ? (ALL_UNITS.find((u) => u.id === selectedBooking.unitId) ?? null)
    : null;

  const handlePrevMonth = useCallback(() => {
    setViewStart((d) => subMonths(d, 1));
  }, []);

  const handleNextMonth = useCallback(() => {
    setViewStart((d) => addMonths(d, 1));
  }, []);

  const handleCellClick = useCallback((date: Date, unit: MockUnit) => {
    // Check if there's already a booking on this cell
    const hit = bookings.find((b) => {
      if (b.unitId !== unit.id) return false;
      return isWithinInterval(date, {
        start: parseISO(b.checkIn),
        end: addDays(parseISO(b.checkOut), -1),
      });
    });
    if (hit) {
      setSelectedBookingId(hit.id);
    } else {
      // Empty cell → stub new booking toast
      setToast({ date, unit });
      setTimeout(() => setToast(null), 4000);
    }
  }, [bookings]);

  const handleStatusChange = useCallback((id: string, status: BookingStatus) => {
    if (status === BookingStatus.CANCELLED) {
      cancelMutation.mutate({ id });
    } else if (status === BookingStatus.CHECKED_IN) {
      checkInMutation.mutate({ id });
    } else if (status === BookingStatus.CHECKED_OUT) {
      checkOutMutation.mutate({ id });
    }
  }, [cancelMutation, checkInMutation, checkOutMutation]);

  const totalWidth = DAYS_IN_VIEW * DAY_COL_WIDTH;

  return (
    <div className="flex flex-col h-full bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
      {/* Toolbar */}
      <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-200 shrink-0">
        <button
          onClick={handlePrevMonth}
          aria-label="Previous month"
          className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 transition-colors"
        >
          <ChevronLeft size={18} aria-hidden="true" />
        </button>

        <h2 className="text-base font-semibold text-gray-800 min-w-[160px] text-center">
          {format(viewStart, 'MMMM yyyy')}
        </h2>

        <button
          onClick={handleNextMonth}
          aria-label="Next month"
          className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 transition-colors"
        >
          <ChevronRight size={18} aria-hidden="true" />
        </button>

        <div className="ml-auto flex items-center gap-2">
          <button
            onClick={() => setViewStart(startOfMonth(today))}
            className="px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 transition-colors"
          >
            Today
          </button>
        </div>
      </div>

      {/* Status legend */}
      <div className="flex items-center gap-4 px-4 py-2 border-b border-gray-100 bg-gray-50 shrink-0 flex-wrap">
        {Object.entries(STATUS_LABELS).map(([status, label]) => (
          <span key={status} className="flex items-center gap-1.5 text-xs text-gray-600">
            <span
              className={cn('inline-block w-3 h-3 rounded-sm', STATUS_BG[status as BookingStatus])}
              aria-hidden="true"
            />
            {label}
          </span>
        ))}
        <span className="ml-2 text-xs text-gray-400 hidden sm:inline">
          Source: A=Airbnb B=Booking.com E=Expedia D=Direct M=Manual
        </span>
      </div>

      {/* Calendar grid */}
      <div className="flex flex-1 overflow-hidden">
        {/* Fixed unit name column */}
        <div
          className="shrink-0 border-r border-gray-200 overflow-hidden flex flex-col"
          style={{ width: UNIT_COL_WIDTH }}
          aria-hidden="true"
        >
          {/* Corner header */}
          <div
            className="shrink-0 border-b border-gray-200 bg-gray-50"
            style={{ height: ROW_HEIGHT }}
          />
          {/* Unit names */}
          <div className="overflow-y-hidden flex-1">
            {ALL_UNITS.map((unit) => (
              <div
                key={unit.id}
                className="flex flex-col justify-center px-3 border-b border-gray-100"
                style={{ height: ROW_HEIGHT }}
              >
                <span className="text-sm font-semibold text-gray-800 truncate">
                  {unit.name}
                </span>
                <span className="text-xs text-gray-400 truncate">
                  {unit.propertyName}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Scrollable date grid */}
        <div
          ref={scrollRef}
          className="flex-1 overflow-auto"
          role="grid"
          aria-label="Reservation calendar"
        >
          {/* Sticky date header row */}
          <div
            className="sticky top-0 z-10 flex bg-gray-50 border-b border-gray-200"
            style={{ minWidth: totalWidth }}
            role="row"
          >
            {days.map((day) => {
              const isToday = isSameDay(day, today);
              return (
                <div
                  key={day.toISOString()}
                  role="columnheader"
                  aria-label={format(day, 'EEEE, MMMM d')}
                  className={cn(
                    'shrink-0 flex flex-col items-center justify-center border-r border-gray-100',
                    isToday && 'bg-primary-50',
                  )}
                  style={{ width: DAY_COL_WIDTH, height: ROW_HEIGHT }}
                >
                  <span
                    className={cn(
                      'text-xs font-medium',
                      isToday ? 'text-primary-700' : 'text-gray-500',
                    )}
                  >
                    {format(day, 'EEE').toUpperCase()}
                  </span>
                  <span
                    className={cn(
                      'text-sm font-bold',
                      isToday
                        ? 'text-white bg-primary-600 rounded-full w-6 h-6 flex items-center justify-center'
                        : 'text-gray-700',
                    )}
                  >
                    {format(day, 'd')}
                  </span>
                </div>
              );
            })}
          </div>

          {/* Unit rows */}
          {ALL_UNITS.map((unit) => {
            const unitBookings = bookings.filter((b) => b.unitId === unit.id);

            return (
              <div
                key={unit.id}
                role="row"
                aria-label={`${unit.name} row`}
                className="relative flex border-b border-gray-100"
                style={{ minWidth: totalWidth, height: ROW_HEIGHT }}
              >
                {/* Day cells — clickable */}
                {days.map((day) => {
                  const isToday = isSameDay(day, today);
                  return (
                    <button
                      key={day.toISOString()}
                      role="gridcell"
                      aria-label={`${unit.name}, ${format(day, 'MMMM d')}`}
                      onClick={() => handleCellClick(day, unit)}
                      className={cn(
                        'shrink-0 h-full border-r border-gray-100 transition-colors',
                        isToday ? 'bg-primary-50/60' : 'hover:bg-gray-50',
                        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-500',
                      )}
                      style={{ width: DAY_COL_WIDTH }}
                    />
                  );
                })}

                {/* Booking blocks — absolutely positioned over the cells */}
                {unitBookings.map((booking) => {
                  const layout = computeBookingLayout(booking, days);
                  if (!layout) return null;

                  const { startOffset, widthPx } = layout;
                  const badge = SOURCE_BADGE[booking.source];
                  const isCancelled = booking.status === BookingStatus.CANCELLED;
                  const isSelected = booking.id === selectedBookingId;

                  return (
                    <button
                      key={booking.id}
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedBookingId(
                          booking.id === selectedBookingId ? null : booking.id,
                        );
                      }}
                      aria-label={`${booking.guest?.fullName ?? booking.guestName ?? 'Guest'}, ${STATUS_LABELS[booking.status]}, ${format(parseISO(booking.checkIn), 'MMM d')} – ${format(parseISO(booking.checkOut), 'MMM d')}`}
                      aria-pressed={isSelected}
                      className={cn(
                        'absolute top-1.5 flex items-center gap-1 px-1.5 rounded-md text-xs font-medium',
                        'overflow-hidden whitespace-nowrap transition-all',
                        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-primary-600',
                        STATUS_BG[booking.status],
                        STATUS_TEXT[booking.status],
                        isCancelled && 'line-through opacity-70',
                        isSelected && 'ring-2 ring-offset-1 ring-primary-600',
                      )}
                      style={{
                        left: startOffset * DAY_COL_WIDTH + 2,
                        width: widthPx,
                        height: ROW_HEIGHT - 12,
                      }}
                    >
                      {/* Source badge */}
                      <span
                        className={cn(
                          'inline-flex items-center justify-center w-4 h-4 rounded text-white text-xs font-bold shrink-0',
                          badge.color,
                        )}
                        aria-hidden="true"
                      >
                        {badge.label}
                      </span>
                      <span className="truncate">{booking.guest?.fullName ?? booking.guestName ?? 'Guest'}</span>
                    </button>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>

      {/* Booking detail slide-over */}
      <BookingDetailSlideOver
        booking={selectedBooking}
        unit={selectedUnit}
        onClose={() => setSelectedBookingId(null)}
        onStatusChange={handleStatusChange}
      />

      {/* New booking toast */}
      {toast && (
        <NewBookingToast
          date={toast.date}
          unit={toast.unit}
          onDismiss={() => setToast(null)}
        />
      )}
    </div>
  );
}
